import React from 'react'
import { ComposedChart } from 'recharts'

import { Bar } from '../../../components/chart/Bar'
import { ChartContext } from '../../../components/chart/ChartWrapper'
import { CursorBar } from '../../../components/chart/Cursor'
import { Label } from '../../../components/chart/Label'
import { Line } from '../../../components/chart/Line'
import { LineDot } from '../../../components/chart/LineDot'
import { ManualGrid } from '../../../components/chart/ManualGrid'
import { Tooltip, TooltipRow } from '../../../components/chart/Tooltip'
import { YAxis } from '../../../components/chart/YAxis'
import { ChartItemColorType } from '../../../components/chart/constants'
import { shortFormatNumber } from '../../../util'
import {
  BAR_GAP,
  BAR_WIDTH,
  BAR_WIDTH_NARROW,
  CHART_HEIGHT,
  CHART_MARGINS,
  DividendValueProperty,
  EXTRA_WIDTH,
  LABELS_MARGIN,
  LABELS_WIDTH,
} from '../constants'
import { DividendsDataType } from '../types'
import { tickFormatter } from '../utils'

export type Props = {
  annualData: DividendsDataType[]
  isCompact: boolean
}

type ChartYearData = { year: string; isEstimate: boolean } & Record<DividendsDataType['id'], number | null>

export function DividendsChart({ annualData, isCompact }: Props) {
  const columnsCount = annualData[0].values.length
  const barWidth = isCompact ? BAR_WIDTH_NARROW : BAR_WIDTH
  const { estimateColumnsCount, hasBarChart } = React.useMemo(
    () => ({
      estimateColumnsCount: annualData[0].values.filter(({ isEstimate }) => isEstimate).length,
      hasBarChart: annualData.some(({ id, isSelected }) => id === DividendValueProperty.Amount && isSelected),
    }),
    [annualData]
  )
  const chartData = React.useMemo(() => {
    const data: Record<string, ChartYearData> = {}
    annualData.forEach(({ id, values }) => {
      values.forEach(({ value, year, isEstimate }) => {
        data[year] = { ...data[year], year, isEstimate, [id]: value }
      })
    })
    return Object.values(data)
  }, [annualData])

  const visibleLeftAxes = React.useMemo(() => annualData.slice(1).filter((group) => group.isSelected), [annualData])

  const chartWidth =
    columnsCount * barWidth + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH + LABELS_WIDTH * 2 * visibleLeftAxes.length
  const estimateAreaWidth = estimateColumnsCount * barWidth + estimateColumnsCount * BAR_GAP
  const barLessChartYMargin = barWidth / 2 + BAR_GAP / 2

  return (
    <div className="flex w-full justify-end">
      <ManualGrid
        visibleLeftAxes={visibleLeftAxes.length}
        estimateAreaWidth={estimateAreaWidth}
        rightOffset={LABELS_WIDTH}
      >
        <ChartContext>
          <ComposedChart
            width={chartWidth}
            height={CHART_HEIGHT}
            data={chartData}
            margin={
              hasBarChart ? CHART_MARGINS : { ...CHART_MARGINS, left: barLessChartYMargin, right: barLessChartYMargin }
            }
            stackOffset="sign" // because we can have negative values
          >
            {annualData.map((row, index) => {
              const axisId = index > 0 ? `left${index}` : 'right'
              const orientation = index > 0 ? 'left' : 'right'
              const tickMargin = index > 0 ? (hasBarChart ? 0 : barLessChartYMargin) : LABELS_MARGIN
              const axisWidth = index > 0 ? LABELS_WIDTH * 2 : LABELS_WIDTH

              // Hide unselected axes but always keep the right axis
              if (!row.isSelected && index > 0) return null

              return (
                <YAxis
                  key={index}
                  orientation={orientation}
                  yAxisId={axisId}
                  tickFormatter={tickFormatter}
                  tickMargin={tickMargin}
                  textAnchor="end"
                  width={axisWidth}
                  interval="preserveStartEnd"
                >
                  {index === 0 ? null : (
                    <Label textAnchor="middle" dx={hasBarChart ? -18 : -barLessChartYMargin - 18}>
                      {row.label}
                    </Label>
                  )}
                </YAxis>
              )
            })}
            <Tooltip
              showLabel={false}
              cursor={<CursorBar numberOfDatapoints={chartData.length} />}
              formatValue={(value: number) => shortFormatNumber(value)}
            >
              {annualData.map(({ id, label, color, isSelected }) =>
                isSelected ? (
                  <TooltipRow indicator={color as ChartItemColorType} key={id} dataKey={id} label={label} />
                ) : null
              )}
            </Tooltip>
            {annualData.map(({ id, isSelected, color }, rowIndex) => {
              if (!isSelected) return null

              const axisId = rowIndex > 0 ? `left${rowIndex}` : 'right'

              if (id === DividendValueProperty.Amount) {
                return (
                  <Bar
                    key={id}
                    dataKey={id}
                    color={(_, payload: ChartYearData) => (payload.isEstimate ? 'gray' : (color as ChartItemColorType))}
                    yAxisId={axisId}
                  />
                )
              }

              return (
                <Line
                  key={id}
                  dataKey={id}
                  yAxisId={axisId}
                  color={color as ChartItemColorType}
                  dot={<LineDot />}
                  activeDot={<LineDot isActive color={color as ChartItemColorType} />}
                />
              )
            })}
          </ComposedChart>
        </ChartContext>
      </ManualGrid>
    </div>
  )
}
